import type { PartialOnly } from '@ipfabric/utils/types/PartialOnly';
import { z } from 'zod';
import {
  type SiteSeparationAttributeTransformation,
  siteSeparationAttributeTransformationSchemaZ,
} from './siteSeparationAttributeTransformation';
import {
  type SiteSeparationRuleCommon,
  siteSeparationRuleCommonSchemaZ,
} from './siteSeparationRuleCommon';

/**
 * Groups cloud resources into sites by the VALUE of a chosen resource tag. `tagNames`
 * (e.g. `Environment`, `Team`) is evaluated in order; the VALUE of the first tag the
 * resource carries becomes its site name (via `transformation` only — no regex).
 *
 * Applies to both `vCloudNetworks` rows (tags via `eCloudNetworkToResourceTag`) and
 * `vDevice` rows not yet migrated to the New Cloud Model (tags in `device.cloud.tags`).
 * Backed by the shared `vCloudResourceTags` table, not the GCP-only `vCloudNetworkTags`.
 */
export interface CloudResourceTagSiteSeparationRule
  extends SiteSeparationRuleCommon {
  type: 'cloudResourceTag';
  /** Selected tag NAMES, evaluated in order; first one present on the network wins. */
  tagNames: string[];
  transformation: SiteSeparationAttributeTransformation;
  /**
   * Also place cloud instances (devices discovered over SSH or a vendor API) into
   * the site of the tagged network they sit in, as regexCloudResourceId/slug do.
   * The tagged resource itself is matched regardless of this flag.
   */
  applyToCloudInstances: boolean;
}

export type CloudResourceTagSiteSeparationRequestRule = PartialOnly<
  CloudResourceTagSiteSeparationRule,
  'id'
>;

export const cloudResourceTagSiteSeparationRuleSchemaZ = z
  .object({
    ...siteSeparationRuleCommonSchemaZ.shape,
    type: z.literal('cloudResourceTag'),
    tagNames: z
      .array(z.string().nonempty())
      .min(1)
      .refine((arr) => new Set(arr).size === arr.length, {
        message: 'Must be unique',
      }),
    transformation: siteSeparationAttributeTransformationSchemaZ,
    applyToCloudInstances: z.boolean(),
  })
  .strict();

export const cloudResourceTagSiteSeparationRequestRuleSchemaZ = z
  .object({
    ...cloudResourceTagSiteSeparationRuleSchemaZ.shape,
    id: cloudResourceTagSiteSeparationRuleSchemaZ.shape.id.optional(),
  })
  .strict();

export function isCloudResourceTagSiteSeparationRule(rule: {
  type?: string;
}): rule is CloudResourceTagSiteSeparationRule {
  return rule.type === 'cloudResourceTag';
}
